<template>
    <b-card>
        <!-- STATUS FORM -->
        <b-form @submit.prevent="confirmUpdate">
            <b-alert variant="info" v-if="suggestedRuleName" show>
                Suggested by rule: {{ suggestedRuleName }}
            </b-alert>
            <b-row>
                <b-col md="6">
                    <b-row>
                        <!-- PRIMARY STATUS -->
                        <b-col md="6">
                            <b-form-group label="Status">
                                <b-form-select v-model="selectedPrimaryStatusId"
                                               :options="primaryStatusOptions"
                                               @change="onPrimaryChange" />
                            </b-form-group>
                        </b-col>
                        <!-- SUB STATUS -->
                        <b-col md="6">
                            <b-form-group label="Sub Status">
                                <b-form-select v-model="selectedSubStatusId"
                                               :options="subStatusOptions"
                                               :disabled="subStatusOptions.length === 0" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Reason">
                        <b-form-select v-model="selectedReasonId"
                                       :options="reasonOptionsWithPlaceholder"
                                       :disabled="!effectiveStatusId || filteredReasonOptions.length === 0" />
                    </b-form-group>
                </b-col>
            </b-row>
            <!-- VALIDATION -->
            <b-alert variant="danger" v-if="validationError" show>
                {{
 validationError
                }}
            </b-alert>

            <div class="text-right">
                <b-button variant="primary"
                          type="submit"
                          :disabled="loading">
                    {{ loading ? 'Updating...' : 'Update Status' }}
                </b-button>
            </div>

        </b-form>

        <!-- HISTORY -->
        <hr />

        <h6>Status History</h6>

        <claim-status-history-display :items="history" :view="2"></claim-status-history-display>

        <!-- CONFIRM MODAL -->
        <b-modal id="confirm-modal"
                 ref="confirmModal"
                 title="Confirm Status Change"
                 @ok="submit">
            <p>
                Change status to <strong>{{ selectedStatusText }}</strong>?
            </p>
        </b-modal>

    </b-card>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import { evaluateClaim } from '../services/claim-status-rules.service.js';
    import ClaimStatusHistoryDisplay from '@/components/claimStatusHistoryDisplay.vue'

    export default {
        props: {
            claim: { type: Object, required: true }
        },
        components: {
            ClaimStatusHistoryDisplay
        },

        data() {
            return {
                selectedPrimaryStatusId: null,
                selectedSubStatusId: null,
                selectedReasonId: null,
                validationError: null,
                suggestedRuleName: null,
                showAllHistory: false,
            };
        },


        watch: {
            filteredReasonOptions(newOptions) {
                if (!newOptions.find(o => o.value === this.selectedReasonId)) {
                    this.selectedReasonId = null;
                }
            }
        },

  computed: {
      ...mapState('claimStatus', ['statusReasons', 'history', 'loading', 'allReasons']),
      ...mapGetters('claimStatus', ['statusOptions']),


      reasonOptionsWithPlaceholder() {
          if (!this.effectiveStatusId) {
              return [{ value: null, text: 'Select a status first' }];
          }

          if (this.filteredReasonOptions.length === 0) {
              return [{ value: null, text: 'No reasons available' }];
          }

          return [
              { value: null, text: 'Please select a reason', disabled: true },
              ...this.filteredReasonOptions
          ];
      },

            filteredReasonOptions() {
                return this.statusReasons
                    .filter(sr =>
                        sr.statusId === this.effectiveStatusId &&
                        this.matchContext(sr)
                    )
                    .map(sr => ({
                        value: sr.reasonId,
                        text: sr.reasonName
                    }));
            },

            selectedStatusText() {
                const s = this.statusOptions.find(x => x.value === this.selectedStatusId);
                return s ? s.text : '';
            },

            statusTree() {
                const map = {};
                const roots = [];

                this.$store.state.claimStatus.statuses.forEach(s => {
                    map[s.id] = { ...s, children: [] };
                });

                Object.values(map).forEach(s => {
                    if (s.parentId) {
                        map[s.parentId].children.push(s);
                    } else {
                        roots.push(s);
                    }
                });

                return roots;
            },

            primaryStatusOptions() {
                return this.statusTree.map(s => ({
                    value: s.id,
                    text: s.name
                }));
            },

            subStatusOptions() {
                if (!this.selectedPrimaryStatusId) return [];

                const parent = this.statusTree.find(
                    s => s.id === this.selectedPrimaryStatusId
                );

                
                var children = parent && parent.children ? parent.children : [];

                return children.map(function (c) {
                    return {
                        value: c.id,
                        text: c.name
                    };
                });

            },

            effectiveStatusId() {
                // final status sent to backend
                return this.selectedSubStatusId || this.selectedPrimaryStatusId;
      },
      
visibleHistory() {
          const limit = this.showAllHistory ? this.history.length : 3;
          return this.history.slice(0, limit);
      }


        },

        methods: {
            ...mapActions('claimStatus', [
                'loadLookups',
                'updateStatus',
                'loadHistory'
            ]),

            matchContext(sr) {
                if (sr.insuranceLineId &&
                    sr.insuranceLineId !== this.claim.insuranceLineId) {
                    return false;
                }
                return true;
            },

            onStatusChange() {
                this.selectedReasonId = null;
            },

            requiresSubStatus() {
                return this.subStatusOptions.length > 0;
            },

    requiresReason(statusId) {
                // simple rule (extend later)
                return this.filteredReasonOptions.length > 0;
            },


            confirmUpdate() {
                this.validationError = null;

                if (!this.selectedPrimaryStatusId) {
                    this.validationError = 'Status is required.';
                    return;
                }

                if (this.requiresSubStatus() && !this.selectedSubStatusId) {
                    this.validationError = 'Sub-status is required.';
                    return;
                }

                if (this.requiresReason(this.effectiveStatusId) && !this.selectedReasonId) {
                    this.validationError = 'Reason is required.';
                    return;
                }

                this.$refs.confirmModal.show();
            },



submit() {
                const payload = {
                    claimId: this.claim.claimId,
                    statusId: this.effectiveStatusId,
                    reasonId: this.selectedReasonId,
                    claim: this.claim
                };

                this.updateStatus(payload);
                this.reset();
            },


            reset() {
                this.selectedStatusId = null;
                this.selectedReasonId = null;
            },

            formatDate(dt) {
                return new Date(dt).toLocaleString();
            },


            onPrimaryChange() {
                this.selectedSubStatusId = null;
                this.selectedReasonId = null;
            },
            getStatusPath(statusId) {
                const map = Object.fromEntries(
                    this.statuses.map(s => [s.id, s])
                );

                let current = map[statusId];
                const path = [];

                while (current) {
                    path.unshift(current.name);
                    current = map[current.parentId];
                }

                return path.join(' → ');
            },

            applyRules() {
                const suggestion = evaluateClaim(this.claim, {
                    statuses: this.$store.state.claimStatus.statuses,
                    reasons: this.allReasons // you may need to store these from service
                });

                console.log('-suggestion: ', suggestion)
                if (!suggestion) return;

                // Set UI selections
                this.setSuggestedStatus(suggestion);
            },
            setSuggestedStatus(suggestion) {

                const targetStatusId = suggestion.statusId;

                const status = this.$store.state.claimStatus.statuses.find(
                    s => s.id === targetStatusId
                );

                if (!status) return;

                // handle hierarchy
                if (status.parentId) {
                    this.selectedPrimaryStatusId = status.parentId;
                    this.selectedSubStatusId = status.id;
                } else {
                    this.selectedPrimaryStatusId = status.id;
                    this.selectedSubStatusId = null;
                }

                this.selectedReasonId = suggestion.reasonId;

                this.suggestedRuleName = suggestion.ruleName;
            }



        },

        async mounted() {
            await this.loadLookups();
            //await this.loadHistory(this.claim.claimId);
            await this.loadHistory(this.claim);

            this.applyRules();
        }
    };
</script>